import { httpClient, handleErrorResp, getOS, getBrowser, getUserAgent, getToken, removeToken } from '../UtilService';
import { ILogin, IStandardAPIResponse, IUpdatePassword } from '../../vm';
import { ensureMsalInitialized, msalInstance, MS_SSO_LOGGED_OUT_PARAM, MS_SSO_MODE_KEY } from '../../authConfig';

export async function loginToApp(
  cred: ILogin
): Promise<IStandardAPIResponse<any>> {
  try {
    // Collect session metadata
    const os = getOS();
    const browser = getBrowser();
    const userAgent = getUserAgent();

    let res = await httpClient<any>(
      `auth-web-login`,
      'POST',
      cred,
      false,
      "application/json; charset=utf-8",
      "", // No token needed for login
      true, // skipTokenRefresh = true for login endpoints
      {
        'X-Client-OS': os,
        'X-Client-Browser': browser,
        'User-Agent': userAgent
      }
    );
    return res;
  } catch (err) {
    return handleErrorResp(err);
  }
}

export async function loginToAppWithMSOffice(
  idToken: string
): Promise<IStandardAPIResponse<any>> {
  try {
    const os = getOS();
    const browser = getBrowser();
    const userAgent = getUserAgent();

    let res = await httpClient<any>(
      `auth-web-login/ms-office`,
      'POST',
      { idToken },
      false,
      "application/json; charset=utf-8",
      "", // No token needed for login
      true, // skipTokenRefresh = true for login endpoints
      {
        'X-Client-OS': os,
        'X-Client-Browser': browser,
        'User-Agent': userAgent
      }
    );
    return res;
  } catch (err) {
    return handleErrorResp(err);
  }
}

/**
 * App-only logout. Clears this app's session + MSAL's *local* cache only — it
 * never calls Microsoft's logout endpoint, so the user stays signed into Entra
 * and the rest of the App Launcher.
 *
 * reason='user'    → explicit logout: redirect to /login?loggedout=1 so the
 *                    login page does NOT silently re-authenticate.
 * reason='session' → involuntary (expired/invalid): redirect to plain /login so
 *                    silent SSO can transparently re-establish the session.
 */
export async function logout(reason: 'user' | 'session' = 'user') {
  try {
    await httpClient<any>(
      `logout`,
      'POST',
      {},
      false,
      "application/json; charset=utf-8",
      getToken(),
      true // skipTokenRefresh
    );
  } catch (error) {
    console.error('Server logout failed, proceeding with client logout:', error);
  } finally {
    removeToken();
    sessionStorage.removeItem(MS_SSO_MODE_KEY);

    try {
      await ensureMsalInitialized();
      if (msalInstance.getAllAccounts().length > 0) {
        // Local cache only — does NOT hit Microsoft's /logout endpoint.
        await msalInstance.clearCache();
      }
    } catch (e) {
      console.error('MSAL cache clear failed (non-fatal):', e);
    }

    window.location.href =
      reason === 'user' ? `/login?${MS_SSO_LOGGED_OUT_PARAM}=1` : '/login';
  }
}

// 🚫 CHANGE PASSWORD — NOT IN USE. The only caller of both is
// ChangePasswordPage, whose route is commented out in components/RoutesMain.tsx,
// so the page can no longer be reached. The two endpoints they post to
// (datacenter-admins/update-password, teams/update-password) are unregistered
// in api/src/routes/config.route.ts, so a call would 404.
//
// Left as live code, not commented: the page body is intact and still
// references them at compile time.
export async function updateAdminPassword(
  cred: IUpdatePassword
): Promise<IStandardAPIResponse<any>> {
  try {
    let res = await httpClient<any>(
      `datacenter-admins/update-password`,
      'POST',
      cred
    );
    return res;
  } catch (err) {
    return handleErrorResp(err);
  }
}
export async function updateTeamRolePassword(
  cred: IUpdatePassword
): Promise<IStandardAPIResponse<any>> {
  try {
    let res = await httpClient<any>(`teams/update-password`, 'POST', cred);
    return res;
  } catch (err) {
    return handleErrorResp(err);
  }
}
// 🚫 PASSWORD EXPIRY — NOT IN USE. Both callers are disabled: the unrouted
// change-password page and the login-page expiry alert (App.tsx /
// LoginMain.tsx). GET last-password-updated-time is unregistered in
// api/src/routes/config.route.ts too, so a call would 404.
//
// Note the server-side 45-day expiry does NOT go through this: user.service.ts
// checks the age directly and is live.
export async function lastPasswordUpdatedTime(): Promise<
  IStandardAPIResponse<{ date?: Date }>
> {
  try {
    let res = await httpClient<{ date?: Date }>(
      `last-password-updated-time`,
      'GET'
    );
    return res;
  } catch (err) {
    return handleErrorResp(err);
  }
}
export async function changeExpiredPasswordForAdmin(
  password: string,
  token?: string
): Promise<IStandardAPIResponse<{ date?: Date }>> {
  try {
    let res = await httpClient<{ date?: Date }>(
      `datacenter-admins/update-expired-password`,
      'PATCH',
      { password },
      false,
      undefined,
      token
    );
    return res;
  } catch (err) {
    return handleErrorResp(err);
  }
}
export async function changeExpiredPasswordForTeam(
  password: string,
  token?: string
): Promise<IStandardAPIResponse<{ date?: Date }>> {
  try {
    let res = await httpClient<{ date?: Date }>(
      `teams/update-expired-password`,
      'PATCH',
      { password },
      false,
      undefined,
      token
    );
    return res;
  } catch (err) {
    return handleErrorResp(err);
  }
}
